// components/project/economic/EconomicRiepilogoView.jsx
// V2 — Riepilogo Direzionale di commessa (dashboard gestionale).
//
// GERARCHIA: KPI PRIMARI → GRAFICO → KPI SECONDARI → DETTAGLIO OPERATIVO → CONTROLLI CONTRATTUALI
//
// DEFINIZIONI AUTORITATIVE (V3 — tutte DERIVATE da valori certificati, nessun cambio backend):
//   - Venduto             = summary.sold_total
//   - Costo di commessa   = Σ max(assigned_cost, actual_cost) [macro-line parent=None]
//   - Margine attuale     = Venduto − Costo di commessa   (su costi conosciuti)
//   - Marginalità venduto%= Margine attuale / Venduto × 100
//   - Ricarico costi %    = Margine attuale / Costo di commessa × 100
//   - Cassa di cantiere   = Incassato − Pagato (indicatore FINANZIARIO, separato dal margine)
//   - Affidato corrente   = Σ assigned_cost (macro-line, 0 per sub risolti/chiusi)
//   - Consuntivato        = summary.actual_cost_total (mostrato come valore, NON come margine)
//   - Residuo da pagare   = max(Consuntivato − Pagato, 0)
// NB: il vecchio "Margine = Venduto − Consuntivato" è RETROCESSO (fuorviante a cantiere in corso).
// Il forecast dei costi non ancora affidati NON è disponibile (estimated_cost sparso/manuale).

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../ui/card';
import {
  TrendingUp, TrendingDown, Euro, Wallet, ShoppingCart, Receipt,
  CircleDollarSign, CheckCircle2, Percent, Info,
} from 'lucide-react';
import ReconciliationCard from './ReconciliationCard';
import StralciSummaryCard from './StralciSummaryCard';
import QeSummaryChart from './QeSummaryChart';
import { salApi } from '../../../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (v) => {
  const n0 = Number(v || 0);
  return (Math.round(n0 * 100) === 0 ? 0 : n0).toLocaleString('it-IT', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 2,
  });
};
const fmtPct = (v) => (v == null ? '—' : `${v.toFixed(1)}%`);
const pct = (num, den) => {
  const d = Number(den || 0);
  if (!d) return null;
  return (Number(num || 0) / d) * 100;
};

const EconomicRiepilogoView = ({ projectId, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [lines, setLines] = useState([]);
  const [error, setError] = useState(null);
  const [contractSummary, setContractSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [sumRes, milestonesRes, linesRes, csRes] = await Promise.all([
          axios.get(`${API}/api/projects/${projectId}/economic-summary`),
          axios.get(`${API}/api/projects/${projectId}/milestones`).catch(() => ({ data: [] })),
          axios.get(`${API}/api/projects/${projectId}/economic-lines`),
          salApi.getContractSummary(projectId).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setSummary(sumRes.data || null);
        const msData = Array.isArray(milestonesRes.data)
          ? milestonesRes.data
          : (milestonesRes.data?.milestones || []);
        setMilestones(msData);
        const linesData = Array.isArray(linesRes.data) ? linesRes.data : (linesRes.data?.lines || linesRes.data?.items || []);
        setLines(linesData);
        setContractSummary(csRes.data || null);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Errore caricamento riepilogo');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (projectId) load();
    return () => { cancelled = true; };
  }, [projectId]);

  const kpi = useMemo(() => {
    const validLines = (lines || []).filter(l => l.status !== 'annullato');
    // Entrate — da milestones (SAL cliente), logica certificata invariata
    const amtOf = (m) => Number(m.invoice_amount) > 0 ? Number(m.invoice_amount) : Number(m.amount || 0);
    const hasInvoiceEvidence = (m) => m.status === 'fatturato' || m.invoiced_at || m.invoice_number || m.invoice_date;
    const isCollected = (m) => m.status === 'incassato' || m.paid_at || m.payment_date;
    const fatturato = milestones.filter(hasInvoiceEvidence).reduce((s, m) => s + amtOf(m), 0);
    const incassato = milestones.filter(isCollected).reduce((s, m) => s + amtOf(m), 0);
    const daMaturare = milestones
      .filter(m => m.status === 'non_sbloccato' || m.status === 'NON_SBLOCCATO')
      .reduce((s, m) => s + Number(m.amount || 0), 0);
    const daIncassare = Math.max(fatturato - incassato, 0);

    const venduto = Number(summary?.sold_total || 0);
    const consuntivato = Number(summary?.actual_cost_total || 0);
    const macroLines = validLines.filter(l => !l.parent_economic_line_id);
    const affidato = macroLines.reduce((s, l) => s + Number(l.assigned_cost || 0), 0);
    // COSTO DI COMMESSA = Σ max(assigned, actual) sulle macro-line (parent=None).
    // Nessun doppio conteggio (è un max), preserva i costi dei sub risolti/chiusi
    // (assigned=0, actual>0) e cattura eventuali sforamenti (actual>assigned).
    const costoDiCommessa = macroLines.reduce(
      (s, l) => s + Math.max(Number(l.assigned_cost || 0), Number(l.actual_cost || 0)), 0);
    const pagato = Number(summary?.installments_paid_total || 0);
    const scadenzeAperte = Number(summary?.installments_remaining_total || 0);

    // MARGINE ATTUALE (su costi conosciuti) = Venduto − Costo di commessa.
    // NB: NON usare più Venduto − Consuntivato come KPI primario (fuorviante col cantiere in corso).
    const margineAttuale = venduto - costoDiCommessa;
    const marginalita = pct(margineAttuale, venduto);                        // — se venduto=0
    const ricarico = costoDiCommessa > 0 ? pct(margineAttuale, costoDiCommessa) : null; // — se costo=0
    const cassa = incassato - pagato;
    const residuoDaPagare = Math.max(consuntivato - pagato, 0);

    return {
      venduto, consuntivato, affidato, costoDiCommessa, pagato, scadenzeAperte,
      fatturato, incassato, daIncassare, daMaturare,
      margineAttuale, marginalita, ricarico, cassa, residuoDaPagare,
      hasConsuntivo: consuntivato > 0,
      hasAffidato: affidato > 0,
      hasCosto: costoDiCommessa > 0,
    };
  }, [summary, milestones, lines]);

  if (loading) {
    return (
      <div className="p-8 text-center text-sm text-[#737373]" data-testid="qe-riepilogo-loading">
        Caricamento riepilogo...
      </div>
    );
  }
  if (error) {
    return (
      <Card className="card-abitalo">
        <CardContent className="p-6 text-center text-sm text-rose-600" data-testid="qe-riepilogo-error">
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 min-w-0" data-testid="qe-riepilogo-view">
      {/* ============= A. KPI PRIMARI ============= */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <PrimaryKpiCard
          testId="kpi-venduto" color="emerald" icon={<Euro className="w-5 h-5" />}
          label="Venduto" value={formatCurrency(kpi.venduto)}
          sub="Ricavi cliente" onClick={() => onNavigate?.('analitico')}
        />
        <PrimaryKpiCard
          testId="kpi-costo-commessa" color="orange" icon={<Wallet className="w-5 h-5" />}
          label="Costo di commessa" value={formatCurrency(kpi.costoDiCommessa)}
          sub="Impegnato / consuntivato"
          muted={!kpi.hasCosto} onClick={() => onNavigate?.('analitico')}
        />
        <PrimaryKpiCard
          testId="kpi-margine-attuale" color={kpi.margineAttuale >= 0 ? 'blue' : 'rose'}
          icon={kpi.margineAttuale >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
          label="Margine attuale" value={formatCurrency(kpi.margineAttuale)}
          sub="Su costi conosciuti"
        />
        <PrimaryKpiCard
          testId="kpi-cassa" color={kpi.cassa >= 0 ? 'emerald' : 'rose'}
          icon={<CircleDollarSign className="w-5 h-5" />}
          label="Cassa di cantiere" value={formatCurrency(kpi.cassa)}
          sub="Incassato − Pagato"
        />
      </div>

      {/* ============= B. GRAFICO PRINCIPALE ============= */}
      <Card className="card-abitalo">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-1 h-5 bg-slate-400 rounded" />
            <h4 className="text-sm font-bold uppercase tracking-wide text-slate-700">Confronto economico di commessa</h4>
          </div>
          <QeSummaryChart
            venduto={kpi.venduto} affidato={kpi.affidato} consuntivato={kpi.consuntivato}
            pagato={kpi.pagato} incassato={kpi.incassato}
          />
        </CardContent>
      </Card>

      {/* ============= C. KPI SECONDARI ============= */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <SecondaryKpiCard
          testId="kpi-marginalita" color="blue" icon={<Percent className="w-4 h-4" />}
          label="Marginalità sul venduto" value={fmtPct(kpi.marginalita)}
          sub="Margine attuale / Venduto" missing={kpi.marginalita == null}
        />
        <SecondaryKpiCard
          testId="kpi-ricarico" color="violet" icon={<TrendingUp className="w-4 h-4" />}
          label="Ricarico sui costi" value={fmtPct(kpi.ricarico)}
          sub="Margine attuale / Costo di commessa" missing={kpi.ricarico == null}
        />
        <SecondaryKpiCard
          testId="kpi-affidato" color="orange" icon={<ShoppingCart className="w-4 h-4" />}
          label="Affidato corrente" value={formatCurrency(kpi.affidato)}
          sub={kpi.hasAffidato ? 'Commitment subappalti attivi' : 'Dopo firma subappalti'}
          missing={!kpi.hasAffidato} onClick={() => onNavigate?.('analitico')}
        />
        <SecondaryKpiCard
          testId="kpi-consuntivato" color="orange" icon={<Wallet className="w-4 h-4" />}
          label="Consuntivato" value={formatCurrency(kpi.consuntivato)}
          sub={kpi.hasConsuntivo ? 'Costo reale (fatturato)' : 'Nessun consuntivo ancora'}
          missing={!kpi.hasConsuntivo} onClick={() => onNavigate?.('analitico')}
        />
      </div>

      {/* ============= D. DETTAGLIO OPERATIVO ============= */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="qe-detail-operativo">
        <DetailBlock
          title="Entrate" accent="bg-emerald-500" testId="qe-detail-entrate"
          rows={[
            { label: 'Fatturato', value: formatCurrency(kpi.fatturato) },
            { label: 'Incassato', value: formatCurrency(kpi.incassato) },
            { label: 'Da incassare', value: formatCurrency(kpi.daIncassare) },
            { label: 'Da maturare', value: formatCurrency(kpi.daMaturare), hint: 'SAL non ancora sbloccati' },
          ]}
        />
        <DetailBlock
          title="Uscite" accent="bg-[#FF7819]" testId="qe-detail-uscite"
          rows={[
            { label: 'Affidato corrente', value: formatCurrency(kpi.affidato) },
            { label: 'Consuntivato', value: formatCurrency(kpi.consuntivato) },
            { label: 'Pagato', value: formatCurrency(kpi.pagato) },
            { label: 'Residuo da pagare', value: formatCurrency(kpi.residuoDaPagare), hint: 'Consuntivato − Pagato' },
            { label: 'Scadenze aperte', value: formatCurrency(kpi.scadenzeAperte), hint: 'Rate/installments non ancora saldati' },
          ]}
        />
      </div>

      {/* ============= E. CONTROLLI CONTRATTUALI ============= */}
      <section data-testid="qe-controlli-contrattuali">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1 h-6 bg-slate-500 rounded" />
          <h4 className="text-sm font-bold uppercase tracking-wide text-slate-700">Controlli contrattuali</h4>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <ReconciliationCard projectId={projectId} />
          <StralciSummaryCard projectId={projectId} summary={contractSummary} />
        </div>
      </section>

      <div className="text-[11px] text-[#a3a3a3] italic px-1 pt-2 border-t border-slate-100">
        <Info className="w-3 h-3 inline mr-1" />
        Dati da: economic-summary, milestones (SAL cliente), economic-lines. Costo di commessa = Σ max(assigned, actual).
        "Costi previsti" e budget preventivo restano disponibili in Analitico.
      </div>
    </div>
  );
};

// ==============================================================
// Cards
// ==============================================================
const ACCENTS = {
  emerald: { icon: 'text-emerald-600', bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-100' },
  orange:  { icon: 'text-[#FF7819]',   bg: 'bg-orange-50',  text: 'text-[#B25400]',   border: 'border-orange-100' },
  blue:    { icon: 'text-blue-600',    bg: 'bg-blue-50',    text: 'text-blue-800',    border: 'border-blue-100' },
  rose:    { icon: 'text-rose-600',    bg: 'bg-rose-50',    text: 'text-rose-800',    border: 'border-rose-100' },
  violet:  { icon: 'text-violet-600',  bg: 'bg-violet-50',  text: 'text-violet-800',  border: 'border-violet-100' },
  slate:   { icon: 'text-slate-500',   bg: 'bg-slate-50',   text: 'text-slate-700',   border: 'border-slate-200' },
};

const PrimaryKpiCard = ({ testId, icon, color = 'slate', label, value, sub, muted = false, onClick }) => {
  const cc = ACCENTS[color] || ACCENTS.slate;
  const clickable = typeof onClick === 'function';
  return (
    <Card
      data-testid={testId}
      className={`card-abitalo ${cc.border} min-w-0 ${clickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={clickable ? onClick : undefined}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-9 h-9 rounded-lg ${cc.bg} flex items-center justify-center flex-shrink-0 ${cc.icon}`}>{icon}</div>
          <div className="text-[11px] uppercase tracking-wide text-[#737373] font-semibold truncate">{label}</div>
        </div>
        <div className={`text-2xl font-bold ${muted ? 'text-[#a3a3a3]' : cc.text} truncate`} title={value}>{value}</div>
        {sub && <div className="text-[11px] text-[#a3a3a3] mt-1 truncate">{sub}</div>}
      </CardContent>
    </Card>
  );
};

const SecondaryKpiCard = ({ testId, icon, color = 'slate', label, value, sub, missing = false, onClick }) => {
  const cc = ACCENTS[color] || ACCENTS.slate;
  const clickable = typeof onClick === 'function';
  return (
    <Card
      data-testid={testId}
      className={`card-abitalo ${cc.border} min-w-0 ${clickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={clickable ? onClick : undefined}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          <div className={`w-8 h-8 rounded-lg ${cc.bg} flex items-center justify-center flex-shrink-0 ${cc.icon}`}>{icon}</div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-[#737373] font-semibold truncate">{label}</div>
            <div className={`text-lg font-bold mt-0.5 ${missing ? 'text-[#a3a3a3]' : cc.text} truncate`}>{value}</div>
            {sub && <div className="text-[10px] text-[#a3a3a3] mt-0.5 truncate">{sub}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const DetailBlock = ({ title, accent, rows, testId }) => (
  <Card className="card-abitalo" data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-1 h-5 ${accent} rounded`} />
        <h5 className="text-xs font-bold uppercase tracking-wide text-slate-600">{title}</h5>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.label} className="flex items-baseline justify-between gap-3 border-b border-dashed border-[#EDEDED] pb-1.5 last:border-b-0" data-testid={`detail-row-${r.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <span className="text-xs text-[#4B5563]" title={r.hint || ''}>{r.label}</span>
            <span className="font-mono text-sm text-slate-800">{r.value}</span>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

export default EconomicRiepilogoView;
